<script setup lang="ts">
import { watch, onUnmounted, computed } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const model = defineModel<boolean>({
  default: false,
  required: true
})

const {
  backdropHide,
  fromTop,
  fromBottom,
  zIndex,
  mask,
  expandable,
  heightFull,
  heightHalf,
  heightQuarter,
  heightPercent,
  maxWidth,
  scrollable
} = defineProps({
  backdropHide: {
    type: Boolean,
    required: false,
    default: false
  },
  fromTop: {
    type: Boolean,
    required: false,
    default: false
  },
  zIndex: {
    type: Number,
    required: false,
    default: 40
  },
  fromBottom: {
    type: Boolean,
    required: false,
    default: false
  },
  mask: {
    type: Boolean,
    required: false,
    default: true
  },
  expandable: {
    type: Boolean,
    required: false
  },
  heightFull: {
    type: Boolean,
    required: false,
    default: false
  },
  heightHalf: {
    type: Boolean,
    required: false,
    default: false
  },
  heightQuarter: {
    type: Boolean,
    required: false,
    default: false
  },
  heightPercent: {
    type: Number,
    required: false,
    default: 0
  },
  maxWidth: {
    type: Number,
    required: false,
    default: 0
  },
  rounded: {
    type: Boolean,
    required: false,
    default: false
  },
  scrollable: {
    type: Boolean,
    required: false,
    default: false
  }
})

const emit = defineEmits(['dismiss'])

function onBackdropClicked() {
  if (backdropHide) {
    model.value = false
    emit('dismiss')
  }
}

const heightStyle = computed(() => {
  if (heightPercent) {
    return { height: `${heightPercent}%` }
  }
  return {}
})
const widthStyle = computed(() => {
  if (maxWidth) {
    // 使用 1/4rem 作为单位，与 Tailwind 保持一致
    return { maxWidth: `${maxWidth * 0.25}rem` }
  }
  return {}
})
</script>
<template>
  <div
    class="fixed w-full h-full left-0 top-0 right-0 bottom-0 duration-300 flex='~ col' items-end justify-center overscroll-contain"
    :class="{ 'opacity-0 pointer-events-none': !model, 'bg-black bg-opacity-50': mask }"
    :style="{ zIndex: zIndex }"
    @click="onBackdropClicked"
    cursor-pointer>
    <div
      class="transition-transform absolute z-40 left-1/2 transform -translate-x-1/2 w-full overscroll-contain"
      flex="~ col"
      justify-center
      items-center
      :class="[
        {
          'translate-y-full': !model,
          'duration-300': model,
          'bottom-0 md:bottom-35': !fromTop,
          '!md:bottom-0': fromBottom,
          'md:top-54': fromTop,
          'max-w-4xl': expandable,
          'max-w-400 max-w-md': !expandable,
          'h-full': heightFull,
          'h-1/2': heightHalf,
          'h-7/8': heightQuarter,
          'rounded-2': rounded
        },
        scrollable ? 'overflow-y-auto overflow-x-hidden' : 'overflow-hidden'
      ]"
      :style="{ ...heightStyle, ...widthStyle }"
      @click.stop>
      <slot></slot>
    </div>
  </div>
</template>
<style lang="scss" scoped></style>
